import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import RecurrenceField from './RecurrenceField'; // Import the new RecurrenceField component

// EmojiPicker component to match the React Native version
const EmojiPicker = ({ emoji, onEmojiChange }) => {
    const emojis = ['💰', '💵', '💸', '💴', '💶', '💷', '🏦', '💳', '💎', '🎁', '🏆'];

    return (
        <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Icon
            </label>
            <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '0.5rem', 
                marginBottom: '0.5rem' 
            }}>
                {emojis.map((emojiOption) => (
                    <button
                        key={emojiOption}
                        type="button"
                        onClick={() => onEmojiChange(emojiOption)}
                        style={{
                            width: '40px',
                            height: '40px',
                            fontSize: '1.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: emojiOption === emoji ? '2px solid #047857' : '1px solid #e5e7eb',
                            borderRadius: '0.375rem',
                            backgroundColor: emojiOption === emoji ? '#ecfdf5' : 'white',
                            cursor: 'pointer'
                        }}
                    >
                        {emojiOption}
                    </button>
                ))}
            </div>
        </div>
    );
};

// WholeNumberCurrencyInput component to match the React Native version
const WholeNumberCurrencyInput = React.forwardRef(({ value, onChangeText, style, ...props }, ref) => {
    const handleChange = (e) => {
        const value = e.target.value.replace(/[^\d]/g, ''); // Remove non-numeric characters
        onChangeText(value);
    };

    return (
        <input
            ref={ref}
            type="text"
            value={value}
            onChange={handleChange}
            style={style}
            {...props}
        />
    );
});

const IncomeForm = ({ onSubmit, onCancel, onDelete, initialValues }) => {
    const [income, setIncome] = useState({
        title: '',
        amount: 0,
        date: moment(),
        recurrenceFrequency: 1,
        recurrencePeriod: 'none',
        emoji: '💰',
    });

    const titleInputRef = useRef(null);
    const amountInputRef = useRef(null);

    useEffect(() => {
        if (initialValues) {
            const updatedIncome = {
                ...initialValues,
                date: moment(initialValues.date),
                amount: Math.floor(initialValues.amount / 100) || 0, // Convert from cents
                recurrenceFrequency: initialValues.recurrenceFrequency || 1,
            };
            setIncome(updatedIncome);
        }

        // Focus on title input when component mounts
        if (titleInputRef.current) {
            titleInputRef.current.focus();
        }
    }, [initialValues]);

    const handleChange = (name, value) => {
        setIncome({ ...income, [name]: value });
    };

    const handleFrequencyChange = (value) => {
        handleChange('recurrenceFrequency', value);
    };

    const handleAmountChange = value => {
        const wholeNumber = parseInt(value, 10);
        handleChange('amount', wholeNumber);
    };

    const getMinDate = () => {
        return moment().startOf('day').toDate();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (income.title && income.amount > 0) {
            // Prepare the income data
            const incomeData = {
                ...income,
                amount: income.amount,  // Convert to cents for storage
                type: 'income',
                date: income.date.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
            };

            if (moment(income.date).isBefore(moment(), 'day')) {
                alert('Please select a date that is not in the past.');
                return;
            }

            // Remove recurrenceFrequency if period is none
            if (incomeData.recurrencePeriod === 'none') {
                delete incomeData.recurrenceFrequency;
            }

            // Show confirmation dialog
            if (window.confirm(`Are you sure you want to add this income?\n\nTitle: ${incomeData.title}\nAmount: $${incomeData.amount}`)) {
                onSubmit(incomeData);
            }
        } else {
            alert('Please enter a title and a valid amount.');
        }
    };

    const handleDelete = () => {
        if (onDelete && window.confirm('Are you sure you want to delete this income?')) {
            onDelete(income);
        }
    };

    return (
        <div style={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%'
        }}>
            <h2 style={{ 
                marginTop: 0, 
                marginBottom: '1.5rem', 
                textAlign: 'center',
                fontWeight: 'bold',
                letterSpacing: '1.1px'
            }}>
                NEW INCOME
            </h2>
            
            <form onSubmit={handleSubmit}>
                <div style={{ 
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <label style={{ 
                        marginBottom: '0.5rem', 
                        fontWeight: 'bold',
                        flex: 1
                    }}>
                        Title
                    </label>
                    <div style={{ flex: 2 }}>
                        <input
                            ref={titleInputRef}
                            type="text"
                            value={income.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            placeholder="e.g., Salary, Freelance"
                            style={{ 
                                width: '100%', 
                                padding: '0.75rem', 
                                borderRadius: '0.375rem',
                                border: '1px solid #e5e7eb',
                                marginBottom: '0.5rem'
                            }}
                            required
                        />
                    </div>
                </div>
                
                {/* Use the EmojiPicker component */}
                <EmojiPicker 
                    emoji={income.emoji} 
                    onEmojiChange={(value) => handleChange('emoji', value)} 
                />
                
                <div style={{ 
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <label style={{ 
                        marginBottom: '0.5rem', 
                        fontWeight: 'bold',
                        flex: 1
                    }}>
                        Amount
                    </label>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.375rem',
                        overflow: 'hidden',
                        flex: 2
                    }}>
                        <span style={{ 
                            padding: '0.75rem', 
                            backgroundColor: '#f3f4f6', 
                            borderRight: '1px solid #e5e7eb' 
                        }}>
                            $
                        </span>
                        <WholeNumberCurrencyInput
                            ref={amountInputRef}
                            value={income.amount.toString()}
                            onChangeText={handleAmountChange}
                            style={{ 
                                flex: 1,
                                padding: '0.75rem',
                                border: 'none',
                                outline: 'none'
                            }}
                            placeholder="0"
                            required
                        />
                    </div>
                </div>
                
                <div style={{ 
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <label style={{ 
                        marginBottom: '0.5rem', 
                        fontWeight: 'bold',
                        flex: 1
                    }}>
                        Date
                    </label>
                    <div style={{ flex: 2 }}>
                        <DatePicker
                            selected={income.date.toDate()}
                            onChange={(date) => handleChange('date', moment(date))}
                            minDate={getMinDate()}
                            dateFormat="MMM d, yyyy"
                            customInput={
                                <input
                                    style={{ 
                                        width: '100%', 
                                        padding: '0.75rem', 
                                        borderRadius: '0.375rem',
                                        border: '1px solid #e5e7eb'
                                    }}
                                />
                            }
                        />
                    </div>
                </div>
                
                {/* Use the new RecurrenceField component */}
                <RecurrenceField
                    frequency={income.recurrenceFrequency}
                    period={income.recurrencePeriod}
                    onFrequencyChange={handleFrequencyChange}
                    onPeriodChange={(value) => handleChange('recurrencePeriod', value)}
                />
                
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    gap: '1rem', 
                    marginTop: '1.5rem' 
                }}>
                    <button
                        type="submit"
                        style={{
                            flex: 1,
                            padding: '0.75rem',
                            backgroundColor: '#047857',
                            color: 'white',
                            fontWeight: 'bold',
                            border: 'none',
                            borderRadius: '0.375rem',
                            cursor: 'pointer'
                        }}
                    >
                        Add Income
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        style={{
                            flex: 1,
                            padding: '0.75rem',
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                            fontWeight: 'bold',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.375rem',
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                </div>
                
                {initialValues && onDelete && (
                    <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                        <button
                            type="button"
                            onClick={handleDelete}
                            style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#fee2e2',
                                color: '#dc2626',
                                fontWeight: 'bold',
                                border: '1px solid #fecaca',
                                borderRadius: '0.375rem',
                                cursor: 'pointer'
                            }}
                        >
                            Delete Income
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
};

export default IncomeForm;